import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const popularDevices = [
  { name: 'Sell iPhone 16 Pro Max', path: '/sell-device/iphone/iphone-16-pro-max' },
  { name: 'Sell iPhone 15 Pro Max', path: '/sell-device/iphone/iphone-15-pro-max' },
  { name: 'Sell iPhone 15 Pro', path: '/sell-device/iphone/iphone-15-pro' },
  { name: 'Sell iPhone 14 Pro Max', path: '/sell-device/iphone/iphone-14-pro-max' },
  { name: 'Sell Galaxy S24 Ultra', path: '/sell-device/samsung/galaxy-s24-ultra' },
  { name: 'Sell Galaxy S23 Ultra', path: '/sell-device/samsung/galaxy-s23-ultra' },
  { name: 'Sell Galaxy Z Fold 5', path: '/sell-device/samsung/galaxy-z-fold-5' },
  { name: 'Sell MacBook Pro M3', path: '/sell-device/macbook/macbook-pro-16-m3' },
  { name: 'Sell MacBook Air M2', path: '/sell-device/macbook/macbook-air-m2' },
  { name: 'Sell iPad Pro M2', path: '/sell-device/ipad/ipad-pro-129-6th' },
  { name: 'Sell PS5', path: '/sell-device/playstation/ps5' },
  { name: 'Sell Xbox Series X', path: '/sell-device/xbox/xbox-series-x' },
];

const categories = [
  { name: 'Sell Phones', path: '/sell/phones' },
  { name: 'Sell Laptops', path: '/sell/laptops' },
  { name: 'Sell Tablets', path: '/sell/tablets' },
  { name: 'Sell Consoles', path: '/sell/consoles' },
  { name: 'Sell Watches', path: '/sell/watches' },
  { name: 'All Devices We Buy', path: '/all-sell' },
];

const nearMeLinks = [
  { name: 'Sell Phone Near Me', path: '/sell-phone-near-me' },
  { name: 'Sell iPhone Near Me', path: '/sell-iphone-near-me' },
  { name: 'Sell Laptop Near Me', path: '/sell-laptop-near-me' },
  { name: 'Sell Phone Chicago', path: '/sell-in/chicago' },
  { name: 'Sell Phone New York', path: '/sell-in/new-york' },
  { name: 'Sell Phone Los Angeles', path: '/sell-in/los-angeles' },
];

const comparisonLinks = [
  { name: 'vs Apple Trade-In', path: '/sell-comparison/iphone-vs-apple-trade-in' },
  { name: 'vs Samsung Trade-In', path: '/sell-comparison/samsung-vs-samsung-trade-in' },
  { name: 'vs Gazelle', path: '/sell-comparison/vs-gazelle' },
  { name: 'vs Decluttr', path: '/sell-comparison/vs-decluttr' },
];

const guideLinks = [
  { name: 'iPhone 16 Pro Max Value', path: '/how-much-is-my-worth/iphone-16-pro-max' },
  { name: 'iPhone 15 Pro Max Value', path: '/how-much-is-my-worth/iphone-15-pro-max' },
  { name: 'Galaxy S25 Ultra Value', path: '/how-much-is-my-worth/galaxy-s25-ultra' },
  { name: 'MacBook Pro M3 Value', path: '/how-much-is-my-worth/macbook-pro-14-m3' },
  { name: 'Sell Before iPhone 17', path: '/sell-guide/sell-before-iphone-17' },
  { name: 'Sell or Keep Calculator', path: '/sell-or-keep-calculator' },
  { name: 'Sell Multiple Devices', path: '/sell-multiple-devices' },
  { name: 'Sustainability & E-Waste', path: '/sustainability' },
  { name: 'iPhone Seller Reviews', path: '/seller-reviews/iphone-sellers' },
];

const brandLinks = [
  { name: 'Sell iPhone', path: '/sell-iphone' },
  { name: 'Sell Samsung Galaxy', path: '/sell-samsung' },
  { name: 'Sell MacBook', path: '/sell-macbook' },
  { name: 'Sell iPad', path: '/sell-ipad' },
  { name: 'Sell AT&T iPhone', path: '/sell-carrier/att-iphone' },
  { name: 'Sell T-Mobile iPhone', path: '/sell-carrier/tmobile-iphone' },
  { name: 'Sell Unlocked Phone', path: '/sell-carrier/unlocked-phones' },
  { name: 'Sell in California', path: '/sell-phone-in/california' },
  { name: 'Sell in Texas', path: '/sell-phone-in/texas' },
  { name: 'Sell in Florida', path: '/sell-phone-in/florida' },
];

const SellInternalLinks = ({ exclude = '' }) => (
  <section className="py-12 border-t border-slate-800" data-testid="sell-internal-links">
    <div className="max-w-6xl mx-auto px-4">
      <h2 className="text-xl font-bold text-white mb-8 text-center">
        More Ways to Sell Your Device
      </h2>

      <div className="grid md:grid-cols-4 gap-8">
        {/* Popular Devices */}
        <div>
          <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-4">Popular Devices</h3>
          <ul className="space-y-2">
            {popularDevices.filter(d => d.path !== exclude).slice(0, 8).map((device, i) => (
              <li key={i}>
                <Link
                  to={device.path}
                  className="flex items-center text-sm text-slate-400 hover:text-green-400 transition-colors"
                >
                  <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  {device.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Categories */}
        <div>
          <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-4">Categories</h3>
          <ul className="space-y-2">
            {categories.filter(c => c.path !== exclude).map((cat, i) => (
              <li key={i}>
                <Link
                  to={cat.path}
                  className="flex items-center text-sm text-slate-400 hover:text-green-400 transition-colors"
                >
                  <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  {cat.name}
                </Link>
              </li>
            ))}
          </ul>

          <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider mt-6 mb-4">Sell Near Me</h3>
          <ul className="space-y-2">
            {nearMeLinks.filter(l => l.path !== exclude).map((link, i) => (
              <li key={i}>
                <Link
                  to={link.path}
                  className="flex items-center text-sm text-slate-400 hover:text-green-400 transition-colors"
                >
                  <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Compare Payouts & Guides */}
        <div>
          <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-4">Compare Payouts</h3>
          <ul className="space-y-2">
            {comparisonLinks.filter(l => l.path !== exclude).map((link, i) => (
              <li key={i}>
                <Link to={link.path} className="flex items-center text-sm text-slate-400 hover:text-green-400 transition-colors">
                  <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Guides & Resources */}
        <div>
          <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-4">Guides & Resources</h3>
          <ul className="space-y-2">
            {guideLinks.filter(l => l.path !== exclude).map((link, i) => (
              <li key={i}>
                <Link to={link.path} className="flex items-center text-sm text-slate-400 hover:text-green-400 transition-colors">
                  <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
          <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider mt-6 mb-4">Sell by Brand & Carrier</h3>
          <ul className="space-y-2">
            {brandLinks.filter(l => l.path !== exclude).map((link, i) => (
              <li key={i}>
                <Link to={link.path} className="flex items-center text-sm text-slate-400 hover:text-green-400 transition-colors">
                  <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export default SellInternalLinks;
